import {
  CheckboxGroup as HeroUICheckboxGroup,
  CheckboxGroupProps as HeroUICheckboxGroupProps,
} from "@heroui/react";
import clsx from "clsx";
import { getKey } from "core/model/utils/strings";
import { Fragment } from "react";
import { useTranslations } from "translations";
import { Checkbox, CheckboxProps } from "../checkbox/checkbox";

export type CheckboxGroupOption<T = string | number> = Omit<
  CheckboxProps,
  "name" | "id" | "value"
> & {
  /**
   * Stable id for a visual section. When set with `sectionHeading`, a heading is
   * rendered before this option if `sectionKey` differs from the previous option.
   */
  sectionKey?: string;
  /** Shown once per `sectionKey` run (compare consecutive options). */
  sectionHeading?: React.ReactNode;
  subForm?: (props: {
    isDisabled?: boolean;
    size?: CheckboxProps["size"];
  }) => React.ReactNode;
  value: T;
};

export type CheckboxGroupProps<T = string | number> = Omit<
  HeroUICheckboxGroupProps,
  | "children"
  | "classNames"
  | "color"
  | "name"
  | "onChange"
  | "onValueChange"
  | "options"
  | "size"
  | "value"
> & {
  color?: CheckboxProps["color"];
  name: string;
  onChange?: (value: T[]) => void;
  onValueChange?: (value: T[]) => void;
  options?: CheckboxGroupOption<T>[];
  size?: CheckboxProps["size"];
  value?: T[];
};

export function CheckboxGroup<T = string | number>({
  onChange,
  onValueChange,
  options,
  size = "md",
  value,
  ...props
}: CheckboxGroupProps<T>) {
  const translations = useTranslations();

  const handleValueChange = (newValue: string[]) => {
    if (!options?.length) return;

    // Convert string values back to original typed values
    const typedValues: T[] = [];
    for (const stringVal of newValue) {
      const option = options.find((opt) => String(opt.value) === stringVal);
      if (option) {
        typedValues.push(option.value);
      }
    }

    onValueChange?.(typedValues);
    onChange?.(typedValues);
  };

  // Convert current typed values to strings for HeroUI
  const stringValues = value?.map((val) => String(val));

  return (
    <HeroUICheckboxGroup
      {...props}
      classNames={{
        base: "leading-inherit",
        description: "text-gray-600",
        errorMessage: "text-danger-dark text-xs",
        label: clsx(
          "text-base leading-inherit font-semibold text-default-900 after:text-gray-900",
          {
            "text-sm": size === "sm" || size === "md",
          },
        ),
        wrapper: clsx("flex", {
          "flex-row gap-6": props.orientation === "horizontal",
        }),
      }}
      data-testid={props.name}
      size={size}
      value={stringValues}
      onValueChange={handleValueChange}
    >
      {options?.map((option, i) => {
        const {
          sectionHeading,
          sectionKey,
          subForm,
          value: optionValue,
          ...checkboxProps
        } = option;
        const prevSectionKey = i > 0 ? options[i - 1]?.sectionKey : undefined;
        const showSectionHeading =
          sectionKey != null &&
          sectionKey !== prevSectionKey &&
          sectionHeading != null &&
          sectionHeading !== "";
        const isSelected = !!(optionValue && value?.includes(optionValue));
        const subFormId = `${props.name}-${optionValue}-subform`;

        return (
          <Fragment key={getKey(String(optionValue), i)}>
            {showSectionHeading && (
              <h3
                className={clsx(
                  "text-small leading-inherit font-semibold text-default-900",
                  i > 0 && "mt-2",
                )}
              >
                {sectionHeading}
              </h3>
            )}
            <div className="flex flex-col gap-3">
              <Checkbox
                {...checkboxProps}
                aria-controls={subForm ? subFormId : undefined}
                aria-expanded={!!subForm && isSelected}
                isDisabled={[props.isDisabled, checkboxProps.isDisabled].some(
                  Boolean,
                )}
                name={props.name}
                size={size}
                value={String(optionValue)}
              />
              {subForm && (
                <>
                  <div
                    className={clsx("mb-2 pl-6 transition-all duration-200", {
                      "hidden opacity-0": !isSelected,
                      "opacity-100": isSelected,
                    })}
                    id={subFormId}
                    role="group"
                  >
                    {subForm({
                      isDisabled: props.isDisabled,
                      size,
                    })}
                  </div>
                  {/* TODO: Screen reader announcement that sub form has been collapsed, should be using a11y hook: https://recare.atlassian.net/browse/DEV-17121 */}
                  <span aria-live="polite" className="sr-only">
                    {isSelected
                      ? translations.accessibility.subFormExpanded
                      : translations.accessibility.subFormCollapsed}
                  </span>
                </>
              )}
            </div>
          </Fragment>
        );
      })}
    </HeroUICheckboxGroup>
  );
}

CheckboxGroup.displayName = "RecareUI.CheckboxGroup";
